'use client';

import { useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Building2, ChevronLeft, ChevronRight, Microscope, Wrench } from 'lucide-react';
import { SiteImage } from '@/components/site-photo';
import { sitePhotos, type SitePhotoKey } from '@/lib/site-imagery';

const slides: { photo: SitePhotoKey; title: string; description: string; icon: typeof Building2 }[] = [
  { photo: 'hero', title: 'Equipped for better care.', description: 'Medical equipment & technical support', icon: Building2 },
  { photo: 'hero-laboratory', title: 'Tools for reliable diagnostics.', description: 'Laboratory equipment & supplies', icon: Microscope },
  { photo: 'hero-technical', title: 'Support throughout the lifecycle.', description: 'Installation, maintenance & engineering', icon: Wrench },
];

export function HeroSlider() {
  const [selected, setSelected] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [viewportRef, carousel] = useEmblaCarousel({ loop: true, duration: reducedMotion ? 0 : 25 });

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(preference.matches);
    update();
    preference.addEventListener('change', update);
    return () => preference.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!carousel) return;
    const update = () => setSelected(carousel.selectedScrollSnap());
    update();
    carousel.on('select', update).on('reInit', update);
    return () => { carousel.off('select', update).off('reInit', update); };
  }, [carousel]);

  return (
    <div className="hero-visual hero-slider" role="region" aria-roledescription="carousel" aria-label="BEDA healthcare solutions">
      <div
        className="hero-slider-viewport"
        ref={viewportRef}
        tabIndex={0}
        aria-label="Use the left and right arrow keys to change the image"
        onKeyDown={event => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'ArrowLeft') { event.preventDefault(); carousel?.scrollPrev(reducedMotion); }
          if (event.key === 'ArrowRight') { event.preventDefault(); carousel?.scrollNext(reducedMotion); }
        }}
      >
        <div className="hero-slides">
          {slides.map(({ photo, title, description, icon: Icon }, index) => (
            <div
              className="hero-slide"
              key={photo}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${slides.length}: ${title}`}
              aria-hidden={index !== selected}
            >
              <SiteImage
                photo={photo}
                className="hero-image"
                sizes="(max-width: 600px) calc(100vw - 40px), (max-width: 900px) calc(100vw - 48px), (max-width: 1100px) calc((100vw - 89px) * .663), (min-width: 1500px) 793px, (min-width: 1392px) 816px, calc((100vw - 162px) * .663)"
                priority={index === 0}
              />
              <span className="photo-note">{sitePhotos[photo].caption}</span>
              <div className="image-caption">
                <Icon size={25} aria-hidden="true" />
                <div><strong>{title}</strong><span>{description}</span></div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <button className="hero-arrow hero-arrow-previous" type="button" aria-label="Previous hero image" onClick={() => carousel?.scrollPrev(reducedMotion)}>
        <ChevronLeft size={23} aria-hidden="true" />
      </button>
      <button className="hero-arrow hero-arrow-next" type="button" aria-label="Next hero image" onClick={() => carousel?.scrollNext(reducedMotion)}>
        <ChevronRight size={23} aria-hidden="true" />
      </button>
      <div className="hero-dots" role="group" aria-label="Choose a hero image">
        {slides.map((slide, index) => (
          <button key={slide.photo} type="button" className={index === selected ? 'is-selected' : ''}
            aria-label={`Show image ${index + 1}: ${slide.title}`} aria-pressed={index === selected}
            onClick={() => carousel?.scrollTo(index, reducedMotion)}><span /></button>
        ))}
      </div>
      <p className="sr-only" aria-live="polite" aria-atomic="true">Image {selected + 1} of {slides.length}: {slides[selected].title}</p>
    </div>
  );
}
